import { PagingParameters } from "@/api/dtos";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabsContained.tsx";
import {
  artworkColumnsFormatted,
  artworkColumnsVisibility,
} from "@/config/dashboard/artwork";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import i18n, { SupportedLocale } from "@/i18n";
import {
  computeFieldDimensionsArtwork,
  computeFieldSignatureArtwork,
  computeFieldSupportTechniqueArtwork,
} from "@/lib/computeFields";
import { getDefaultImage } from "@/lib/image";
import { Artwork } from "@/lib/types";
import { parseJsonOrUndefined } from "@/lib/utils";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import {
  ChevronLeft,
  LayoutGrid,
  PlusCircle,
  PlusIcon,
  Table2,
  Trash2Icon,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { Button } from "../ui/button";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";

const STORAGE_KEY = "artwork-links-table-preferences";

interface ArtworkLinksProps {
  id: string;
  type: LinkableType;
}

/**
 * ArtworkLinks component
 * @returns {JSX.Element}
 */
const ArtworkLinks = ({ id, type }: ArtworkLinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedArtworks, setPaginationRelatedArtworks] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: artworksRelated } = useGetRelatedFields(
    id,
    type,
    "relatedArtwork",
    getPaginationRelatedArtworks.pageSize,
    getPaginationRelatedArtworks.page * getPaginationRelatedArtworks.pageSize,
  );

  const { data: countRelatedArtworks } = useCountRelatedFields(id, type, [
    "relatedArtwork",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedArtwork",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedArtwork",
  );

  const [isAddMode, setIsAddMode] = useState(false);
  const [searchContent, setSearchContent] = useState("");

  const [getPaginationArtworksToAdd, setPaginationArtworksToAdd] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const { data: notAlreadyRelatedArtworks } = useGetNotRelatedResource<Artwork>(
    {
      resultType: "artwork",
      resourceId: id,
      pagination: getPaginationArtworksToAdd,
      searchQuery: searchContent,
    },
  );

  const columns: ColumnDef<Artwork, any>[] = useMemo(() => {
    const handleCreateRelatedArtworks = (artworkId: string) => {
      try {
        createRelatedFields([id, artworkId]);
      } catch (error) {
        console.error(error);
      }
    };

    const handleDeleteRelation = (artworkId: string) => {
      deleteRelation(artworkId);
    };

    return [
      ...generateTable(artworkColumnsFormatted, "artwork"),
      {
        accessorKey: "actions",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Artwork> }) => {
          if (isAddMode) {
            return (
              <div className="flex items-center gap-1 justify-end mr-2">
                <ShowElements
                  type="artwork"
                  elements={[
                    {
                      label: t("oeuvresDescriptionTitre"),
                      value: row.original.oeuvresDescriptionTitre,
                      type: "text",
                    },
                    {
                      label: t("oeuvresDescriptionNumeroEnregistrement"),
                      value:
                        row.original.oeuvresDescriptionNumeroEnregistrement,
                      type: "text",
                    },
                    {
                      label: t("oeuvresDescriptionResume"),
                      value: row.original.oeuvresDescriptionResume,
                      type: "richtext",
                    },
                    {
                      label: t("attachments"),
                      value: row.original.attachments,
                      type: "image",
                    },
                  ]}
                  onAdd={() => handleCreateRelatedArtworks(row.original.id)}
                />
                <Button
                  className="font-semibold flex gap-2 p-1"
                  variant="ghost"
                  onClick={() => handleCreateRelatedArtworks(row.original.id)}
                  type="button"
                >
                  <PlusCircle className="w-5 h-5 cursor-pointer text-[#005D8D]" />
                </Button>
              </div>
            );
          }
          return (
            <div className="flex items-center gap-1 justify-end mr-2">
              <Button
                className="font-semibold flex gap-2 p-1"
                variant="ghost"
                type="button"
                onClick={() => handleDeleteRelation(row.original.id)}
              >
                <Trash2Icon className="w-5 h-5 cursor-pointer text-gray-500" />
              </Button>
              <ShowElements
                type="artwork"
                elements={[
                  {
                    label: t("oeuvresDescriptionTitre"),
                    value: row.original.oeuvresDescriptionTitre,
                    type: "text",
                  },
                  {
                    label: t("oeuvresDescriptionNumeroEnregistrement"),
                    value: row.original.oeuvresDescriptionNumeroEnregistrement,
                    type: "text",
                  },
                  {
                    label: t("oeuvresDescriptionResume"),
                    value: row.original.oeuvresDescriptionResume,
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: row.original.attachments,
                    type: "image",
                  },
                ]}
              />
            </div>
          );
        },
      },
    ];
  }, [isAddMode, t, createRelatedFields, deleteRelation, id]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const { visibility } = JSON.parse(saved);
        return (
          visibility || {
            ...artworkColumnsVisibility,
            oeuvresDescriptionVignette: true,
            oeuvresPublicationStatus: false,
          }
        );
      }
      return {
        ...artworkColumnsVisibility,
        oeuvresDescriptionVignette: true,
        oeuvresPublicationStatus: false,
      };
    },
  );

  const flatVisibleArtworkColumns = artworkColumnsFormatted.flatMap(
    (column: ColumnFormatted) => (column.visible ? column.key : []),
  );

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const { order } = JSON.parse(saved);
      return order || flatVisibleArtworkColumns;
    }
    return flatVisibleArtworkColumns;
  });

  return (
    <div className="w-full">
      <Tabs defaultValue="list">
        {isAddMode && (
          <div
            className="flex items-center gap-1 mb-4 cursor-pointer"
            onClick={handleAddMode}
          >
            <ChevronLeft className="w-3 h-3 text-gray-300" />
            <p className="text-sm text-[#006EAB] hover:text-[#005D8D]">
              {t("linkedEntries")}
            </p>
          </div>
        )}
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-lg font-semibold">{t("linkedArtworks")}</h2>
          {!isAddMode && (
            <div className="flex gap-2 items-center">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="grid">
                  <LayoutGrid className="h-4 w-4" />
                </TabsTrigger>
                <TabsTrigger value="list">
                  <Table2 className="h-4 w-4" />
                </TabsTrigger>
              </TabsList>
              <Button variant="outline" onClick={handleAddMode}>
                <PlusIcon className="w-4 h-4 mr-2" />
                {t("add")}
              </Button>
            </div>
          )}
          {isAddMode && (
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
              {/* <Button variant="default" onClick={handleAddMode}>
                <PlusIcon className="w-4 h-4 mr-2" />
                {t("newArtwork")}
              </Button> */}
            </div>
          )}
        </div>

        {isAddMode && (
          <DataTable
            columns={columns}
            data={notAlreadyRelatedArtworks?.data ?? []}
            pagination={{
              pagination: getPaginationArtworksToAdd,
              setPage: setPaginationArtworksToAdd,
              totalSize: notAlreadyRelatedArtworks?.totalSize ?? 0,
            }}
            storageKey={STORAGE_KEY}
            options={{
              enableColumnResizing: true,
              onColumnVisibilityChange: setColumnVisibility,
              onColumnOrderChange: setColumnsOrder,
              state: {
                columnVisibility: columnVisibility,
                columnOrder: columnsOrder,
              },
            }}
          />
        )}
        <TabsContent value="list">
          {!isAddMode && (
            <DataTable
              columns={columns}
              data={artworksRelated ?? []}
              pagination={{
                pagination: getPaginationRelatedArtworks,
                setPage: setPaginationRelatedArtworks,
                totalSize: countRelatedArtworks?.relatedArtwork,
              }}
              storageKey={STORAGE_KEY}
              options={{
                enableColumnResizing: true,
                onColumnVisibilityChange: setColumnVisibility,
                onColumnOrderChange: setColumnsOrder,
                state: {
                  columnVisibility: columnVisibility,
                  columnOrder: columnsOrder,
                },
              }}
            />
          )}
        </TabsContent>
        <TabsContent value="grid">
          <div className="w-full flex gap-8 flex-wrap">
            {!isAddMode &&
              artworksRelated?.map((artwork: Artwork) => {
                return (
                  <div
                    key={artwork.id}
                    className={`h-[389px] flex-col justify-start items-start inline-flex max-w-[300px]`}
                  >
                    <img
                      className="h-[251px] relative"
                      src={
                        getDefaultImage(artwork.attachments ?? [], "artwork") ||
                        ""
                      }
                      alt="image illustration"
                    />
                    <div className="self-stretch h-[30px] py-2 flex-col justify-start items-start gap-1 flex">
                      <div>
                        <span className="text-slate-600 text-xs font-normal font-['Instrument Sans'] leading-[14.40px]">
                          N° d’enregistrement{" "}
                        </span>
                        <span className="text-[#090909] text-xs text-left font-semibold font-['Instrument Sans'] leading-[14.40px]">
                          {artwork.oeuvresDescriptionNumeroEnregistrement}
                        </span>
                      </div>
                    </div>
                    <div className="self-stretch h-[108px] py-2 flex-col justify-start items-start gap-1 flex">
                      <p className="text-start self-stretch text-[#090909] text-xs font-normal font-['Instrument Sans'] leading-[14.40px]">
                        {artwork.relatedAuthor &&
                        artwork.relatedAuthor.length > 0
                          ? `${artwork.relatedAuthor[0].artistesAuteursDescriptionPrenom || ""} ${artwork.relatedAuthor[0].artistesAuteursDescriptionNom || ""}`
                          : "Auteur inconnu"}
                      </p>
                      <p className="text-left leading-tight">
                        <span className="text-[#090909] w-full text-base text-left font-semibold font-['Instrument Sans'] leading-tight">
                          {artwork.oeuvresDescriptionTitre},{" "}
                        </span>
                        <span className="text-[#090909] text-base text-left font-normal font-['Instrument Sans'] leading-tight">
                          {artwork.oeuvresDescriptionDateDeCreation}
                        </span>
                      </p>
                      <div
                        className="text-[#090909] text-xs font-normal font-['Instrument Sans'] leading-[14.40px]"
                        dangerouslySetInnerHTML={{
                          __html: computeFieldDimensionsArtwork(
                            {
                              oeuvresDescriptionDimensionsOeuvreRaw:
                                artwork.oeuvresDescriptionDimensionsOeuvreRaw,
                              oeuvresDescriptionDimensionsOeuvreInconnues:
                                artwork.oeuvresDescriptionDimensionsOeuvreInconnues,
                              oeuvresDescriptionDimensionsOeuvre:
                                parseJsonOrUndefined(
                                  artwork.oeuvresDescriptionDimensionsOeuvre,
                                ),
                            },
                            {
                              language: i18n.language as SupportedLocale,
                            },
                          ),
                        }}
                      ></div>
                      <p className="text-start self-stretch text-[#090909] text-xs font-normal font-['Instrument Sans'] leading-[14.40px]">
                        {computeFieldSupportTechniqueArtwork(artwork, {
                          language: i18n.language as SupportedLocale,
                        })}
                      </p>
                      <p className="text-start self-stretch h-[15px] text-[#090909] text-xs font-normal font-['Instrument Sans'] leading-[14.40px]">
                        {computeFieldSignatureArtwork(artwork, {
                          language: i18n.language as SupportedLocale,
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ArtworkLinks;
